import type {
    AdsStateTO as AdsStateTOType,
    AttributeErrorTOTypeEnum as AttributeErrorTOTypeEnumType,
    AttributeWriteBooleanTO,
    AttributeWriteDoubleTO,
    AttributeWriteLongTO,
    AttributeWriteMrmStateTO,
    AttributeWritePositionTO,
    DrivingGearTO as DrivingGearTOType,
    MissionStateTO as MissionStateTOType,
    MrmExecutionPhaseTO as MrmExecutionPhaseTOType,
    MrmStateNameTO as MrmStateNameTOType,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import {
    AdsStateTO,
    AttributeErrorTOTypeEnum,
    DrivingGearTO,
    MissionStateTO,
    MrmExecutionPhaseTO,
    MrmStateNameTO,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import NumberInput from '@rio-cloud/rio-uikit/NumberInput';
import RIOSelect from '@rio-cloud/rio-uikit/Select';
import Switch from '@rio-cloud/rio-uikit/Switch';

// ─── helpers ────────────────────────────────────────────────────────────────

function toOptions<T extends string>(values: readonly T[]): { id: T; label: string }[] {
    return values.map(v => ({ id: v, label: v }));
}

export const ADS_STATE_OPTIONS = toOptions(Object.values(AdsStateTO) as AdsStateTOType[]);
export const DRIVING_GEAR_OPTIONS = toOptions(Object.values(DrivingGearTO) as DrivingGearTOType[]);
export const MISSION_STATE_OPTIONS = toOptions(Object.values(MissionStateTO) as MissionStateTOType[]);
const MRM_STATE_OPTIONS = toOptions([...Object.values(MrmStateNameTO)] as MrmStateNameTOType[]);
const MRM_PHASE_OPTIONS = [
    { id: '' as const, label: '— none —' },
    ...toOptions(Object.values(MrmExecutionPhaseTO) as MrmExecutionPhaseTOType[]),
];
const ERROR_MODE_OPTIONS = toOptions(Object.values(AttributeErrorTOTypeEnum) as AttributeErrorTOTypeEnumType[]);

// ─── shared select wrapper ───────────────────────────────────────────────────

function SimSelect<T extends { id: string; label: string }>({
    options,
    value,
    onChange,
    placeholder,
}: {
    options: T[];
    value: string;
    onChange: (opt: T | undefined) => void;
    placeholder?: string;
}) {
    return (
        <RIOSelect
            options={options}
            value={value ? [value] : undefined}
            onChange={onChange}
            placeholder={placeholder}
            dropdownClassName='sim-attr-dropdown'
        />
    );
}

// ─── ErrorModeSelect ────────────────────────────────────────────────────────

interface ErrorModeSelectProps {
    value: string;
    onChange: (errorType: string) => void;
}

export const ErrorModeSelect = ({ value, onChange }: ErrorModeSelectProps) => (
    <SimSelect
        options={ERROR_MODE_OPTIONS}
        value={value}
        onChange={opt => onChange(opt?.id ?? '')}
        placeholder='Select error type…'
    />
);

// ─── SpeedControl ───────────────────────────────────────────────────────────

interface SpeedControlProps {
    entry: AttributeWriteDoubleTO | undefined;
    onValueChange: (patch: AttributeWriteDoubleTO) => void;
}

export const SpeedControl = ({ entry, onValueChange }: SpeedControlProps) => (
    <NumberInput
        value={entry?.value}
        noDefault
        unit='m/s'
        min={-999}
        max={999}
        step={0.1}
        digitPrecision={2}
        onChange={v => {
            if (v !== undefined) onValueChange({ value: v });
        }}
    />
);

// ─── PositionControl ─────────────────────────────────────────────────────────

interface PositionControlProps {
    entry: AttributeWritePositionTO | undefined;
    onValueChange: (patch: AttributeWritePositionTO) => void;
}

export const PositionControl = ({ entry, onValueChange }: PositionControlProps) => {
    const lat = entry?.value?.latitude;
    const lng = entry?.value?.longitude;
    return (
        <div className='AttributeEditControl__positionInputs'>
            <NumberInput
                value={lat}
                noDefault
                unit='lat'
                min={-90}
                max={90}
                step={0.000001}
                digitPrecision={6}
                onChange={v => {
                    if (v !== undefined) onValueChange({ value: { latitude: v, longitude: lng ?? 0 } });
                }}
            />
            <NumberInput
                value={lng}
                noDefault
                unit='lng'
                min={-180}
                max={180}
                step={0.000001}
                digitPrecision={6}
                onChange={v => {
                    if (v !== undefined) onValueChange({ value: { latitude: lat ?? 0, longitude: v } });
                }}
            />
        </div>
    );
};

// ─── EnumSelectControl ───────────────────────────────────────────────────────

interface EnumSelectControlProps<T extends string> {
    entry: { value?: T; error?: unknown; pinned?: boolean } | undefined;
    options: { id: T; label: string }[];
    onValueChange: (patch: { value?: T }) => void;
}

export function EnumSelectControl<T extends string>({ entry, options, onValueChange }: EnumSelectControlProps<T>) {
    return (
        <SimSelect
            options={options as { id: string; label: string }[]}
            value={entry?.value ?? ''}
            onChange={opt => onValueChange({ value: (opt?.id as T | undefined) ?? undefined })}
            placeholder='Select…'
        />
    );
}

// ─── BooleanToggleControl ─────────────────────────────────────────────────────

interface BooleanToggleControlProps {
    entry: AttributeWriteBooleanTO | undefined;
    onValueChange: (patch: AttributeWriteBooleanTO) => void;
}

export const BooleanToggleControl = ({ entry, onValueChange }: BooleanToggleControlProps) => (
    <Switch
        checked={entry?.value ?? false}
        onChange={(checked: boolean) => onValueChange({ value: checked })}
        enabledText='On'
        disabledText='Off'
    />
);

// ─── MrmStateControl ─────────────────────────────────────────────────────────

interface MrmStateControlProps {
    entry: AttributeWriteMrmStateTO | undefined;
    onValueChange: (patch: AttributeWriteMrmStateTO) => void;
}

export const MrmStateControl = ({ entry, onValueChange }: MrmStateControlProps) => {
    const mrmVal = entry?.value ?? {};
    const isPerforming = mrmVal.state === MrmStateNameTO.Performing;
    return (
        <div className='AttributeEditControl__mrmInputs'>
            <SimSelect
                options={MRM_STATE_OPTIONS as { id: string; label: string }[]}
                value={mrmVal.state ?? ''}
                onChange={opt => {
                    const newState = (opt?.id as MrmStateNameTOType) || undefined;
                    onValueChange({
                        value: {
                            ...mrmVal,
                            state: newState,
                            executionPhase: newState === MrmStateNameTO.Performing ? mrmVal.executionPhase : undefined,
                        },
                    });
                }}
                placeholder='State…'
            />
            {isPerforming && (
                <SimSelect
                    options={MRM_PHASE_OPTIONS as { id: string; label: string }[]}
                    value={mrmVal.executionPhase ?? ''}
                    onChange={opt =>
                        onValueChange({
                            value: {
                                ...mrmVal,
                                executionPhase: (opt?.id as MrmExecutionPhaseTOType) || undefined,
                            },
                        })
                    }
                    placeholder='Phase…'
                />
            )}
        </div>
    );
};

// ─── LatencyControl ───────────────────────────────────────────────────────────

interface LatencyControlProps {
    entry: AttributeWriteLongTO | undefined;
    onValueChange: (patch: AttributeWriteLongTO) => void;
}

export const LatencyControl = ({ entry, onValueChange }: LatencyControlProps) => (
    <NumberInput
        value={entry?.value}
        noDefault
        unit='ms'
        min={0}
        step={1}
        digitPrecision={0}
        onChange={v => {
            if (v !== undefined) onValueChange({ value: Math.round(v) });
        }}
    />
);
